import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Crown, 
  Video, 
  HeadphonesIcon, 
  BookCheck, 
  Check, 
  MessageCircle,
  Users,
  Bot,
  FileText,
  Camera,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

export const MembrePage: React.FC = () => {
  const features = [
    {
      icon: Video,
      title: "7 Vidéos explicatives",
      description: "Collection de vidéos pédagogiques pour votre apprentissage"
    },
    {
      icon: HeadphonesIcon,
      title: "Aide 7j/7",
      description: "Support pédagogique disponible pour répondre à vos questions"
    },
    {
      icon: BookCheck,
      title: "Cours et exercices",
      description: "Accès aux cours HTML interactifs et exercices pratiques"
    },
    {
      icon: Bot,
      title: "Chatbot virtuel",
      description: "Assistant IA pour vous aider dans vos études"
    },
    {
      icon: MessageCircle,
      title: "Forums de discussion",
      description: "Participez aux discussions et posez vos questions avec images"
    },
    {
      icon: FileText,
      title: "Documents PDF",
      description: "Accès aux ressources PDF téléchargeables"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
              <Crown className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-5xl font-bold text-secondary">
              Sprintbac Membre
            </h1>
          </div>
          <p className="text-xl text-muted-foreground mb-8">
            Accédez à toutes les fonctionnalités de la plateforme
          </p>
          
          {/* VIDÉO DE PRÉSENTATION - VERSION SIMPLE QUI MARCHE */}
          <Card className="border-0 sprintbac-shadow max-w-4xl mx-auto mb-8">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-semibold text-secondary mb-3">
                  🎥 Vidéo de présentation Sprintbac
                </h3>
                <p className="text-muted-foreground">
                  Découvrez toutes les fonctionnalités de la plateforme en vidéo
                </p>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-yellow-50 rounded-lg p-8 text-center">
                <Video className="h-24 w-24 text-primary mx-auto mb-6" />
                <h4 className="text-xl font-semibold text-secondary mb-4">
                  Présentation complète de Sprintbac
                </h4>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Regardez notre vidéo pour découvrir comment Sprintbac peut transformer votre apprentissage avec ses outils innovants et son interface intuitive.
                </p>
                
                {/* ZONE POUR LIEN VIDÉO - À CONFIGURER PAR L'ADMIN */}
                <div className="bg-white rounded-lg p-6 border-2 border-dashed border-gray-300 mb-6">
                  <h5 className="font-semibold text-gray-700 mb-3">
                    📝 Configuration pour l'administrateur
                  </h5>
                  <div className="text-left bg-gray-50 p-4 rounded">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Étape 1 :</strong> Uploadez votre vidéo sur Google Drive
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Étape 2 :</strong> Rendez-la publique (Partager → Accessible à tous)
                    </p>
                    <p className="text-sm text-gray-600 mb-3">
                      <strong>Étape 3 :</strong> Remplacez le bouton ci-dessous par :
                    </p>
                    <code className="text-xs bg-gray-100 p-2 rounded block">
                      &lt;a href="VOTRE_LIEN_GOOGLE_DRIVE" target="_blank" className="..."&gt;<br/>
                      &nbsp;&nbsp;Regarder la présentation<br/>
                      &lt;/a&gt;
                    </code>
                  </div>
                </div>
                
                {/* BOUTON TEMPORAIRE - À REMPLACER PAR LE VRAI LIEN */}
                <Button 
                  className="sprintbac-gradient hover:opacity-90 text-white px-8 py-4 text-lg"
                  onClick={() => {
                    alert('🎥 Vidéo bientôt disponible !\n\nL\'administrateur doit configurer le lien Google Drive.\n\nConsultez les instructions ci-dessus.');
                  }}
                >
                  <ExternalLink className="mr-3 h-5 w-5" />
                  Regarder la présentation
                </Button>
                
                <p className="text-xs text-gray-500 mt-4">
                  Cliquez pour voir les instructions de configuration
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* Prix */}
          <Card className="sprintbac-glow border-0 bg-white/80 backdrop-blur-sm max-w-md mx-auto">
            <CardContent className="p-8">
              <div className="text-center">
                <div className="text-6xl font-bold text-secondary mb-2">
                  300 DH
                  <span className="text-lg text-muted-foreground font-normal">/mois</span>
                </div>
                <p className="text-muted-foreground mb-6">
                  Accès complet à toutes les fonctionnalités
                </p>
                <Button className="sprintbac-gradient hover:opacity-90 text-white px-8 py-3 text-lg">
                  <Crown className="mr-2 h-5 w-5" />
                  Devenir Membre
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {features.map((feature, index) => (
            <Card key={index} className="border-0 sprintbac-shadow hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-secondary">
                        {feature.title}
                      </h3>
                      <Badge className="bg-green-100 text-green-800 text-xs">
                        Disponible
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Detailed Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Forums avec photos */}
          <Card className="border-0 sprintbac-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Forums interactifs
              </CardTitle>
              <CardDescription>
                Communauté d'entraide entre étudiants
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Questions anonymes</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Réponses de la communauté</span>
                </div>
                <div className="flex items-center gap-3">
                  <Camera className="h-4 w-4 text-blue-600" />
                  <span className="text-sm">Partage d'images dans les questions</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Réponses des enseignants</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Chat IA */}
          <Card className="border-0 sprintbac-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                Assistant IA
              </CardTitle>
              <CardDescription>
                Chatbot intelligent pour vos études
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Réponses instantanées</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Aide aux devoirs</span>
                </div>
                <div className="flex items-center gap-3">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Explications détaillées</span>
                </div>
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  <span className="text-sm text-orange-700">Nécessite une clé API OpenAI</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Statistiques réelles */}
        <Card className="border-0 bg-gradient-to-r from-blue-50 to-yellow-50 mb-12">
          <CardContent className="p-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-secondary mb-6">
                Fonctionnalités Membre
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">7</div>
                  <p className="text-sm text-muted-foreground">Vidéos Google Drive</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">✓</div>
                  <p className="text-sm text-muted-foreground">Chat IA fonctionnel</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-2">PDF</div>
                  <p className="text-sm text-muted-foreground">Documents téléchargeables</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Final */}
        <div className="text-center">
          <Card className="sprintbac-glow border-0 bg-white/80 backdrop-blur-sm max-w-2xl mx-auto">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-secondary mb-4">
                Prêt à accéder aux fonctionnalités Membre ?
              </h3>
              <p className="text-muted-foreground mb-6">
                Débloquez l'accès complet à la plateforme Sprintbac
              </p>
              <Button className="sprintbac-gradient hover:opacity-90 text-white px-12 py-4 text-xl">
                <Crown className="mr-3 h-6 w-6" />
                S'abonner - 300 DH/mois
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                Accès immédiat • Support technique inclus
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};